import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ApiError } from '../../../shared/api/error';
import { useLoginMutation, type LoginPayload } from '../api/useSession';

// Phone: E.164-ish with a lenient upper bound so the form doesn't reject
// legitimate international numbers we haven't seen yet. The server is
// the source of truth on shape.
// PIN: 4 digits in v1 (matches the seed). Complexity policy is out of
// scope for P1 (phase-1.md §8).
const schema = z.object({
  phone: z
    .string()
    .trim()
    .regex(/^\+\d{6,15}$/, { message: 'form.phone_invalid' }),
  pin: z.string().regex(/^\d{4}$/, { message: 'form.pin_invalid' }),
});

type FormValues = z.infer<typeof schema>;

// Fixed PIN length for v1. The keypad caps digit entry here so the dots
// and the ✓ button are always in sync.
const PIN_LENGTH = 4;

export interface LoginFormProps {
  onSuccess?: () => void;
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const { t } = useTranslation();
  const login = useLoginMutation();
  const [digits, setDigits] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { phone: '', pin: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    setValue('pin', digits.join(''), { shouldValidate: false });
  }, [digits, setValue]);

  function addDigit(d: string) {
    setDigits((prev) => (prev.length < PIN_LENGTH ? [...prev, d] : prev));
  }

  function deleteDigit() {
    setDigits((prev) => prev.slice(0, -1));
  }

  async function onSubmit(values: FormValues) {
    try {
      await login.mutateAsync(values satisfies LoginPayload);
      onSuccess?.();
    } catch {
      // error exposed via login.error; form stays unlocked automatically
    }
  }

  const serverError = login.error instanceof ApiError ? login.error : null;
  const serverMessage = serverError
    ? t(serverError.i18nKey, { defaultValue: t('errors.unknown') })
    : null;

  const submitting = isSubmitting || login.isPending;

  return (
    <form className="stack" onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* Phone field */}
      <div className="field">
        <label htmlFor="lf-phone">{t('auth.phone')}</label>
        <input
          id="lf-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+…"
          disabled={submitting}
          aria-invalid={errors.phone ? 'true' : 'false'}
          {...register('phone')}
        />
        {errors.phone ? (
          <p className="field__error" role="alert">
            {t(errors.phone.message ?? 'form.phone_invalid', {
              defaultValue: t('errors.validation'),
            })}
          </p>
        ) : null}
      </div>

      {/* PIN section */}
      <div className="field">
        <label>{t('auth.pin')}</label>

        {/* Dot display — 4 circles showing how many digits are entered */}
        <div
          aria-live="polite"
          aria-label={t('auth.pin_display_aria', { count: digits.length })}
          style={{
            display: 'flex',
            gap: 20,
            justifyContent: 'center',
            padding: '16px 0 12px',
          }}
        >
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <span
              key={i}
              aria-hidden="true"
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: i < digits.length ? 'var(--ink)' : 'transparent',
                border: '2px solid var(--ink)',
                display: 'inline-block',
                transition: 'background 0.1s',
              }}
            />
          ))}
        </div>

        {/* Hidden input carries the PIN for react-hook-form validation */}
        <input type="hidden" {...register('pin')} />

        {errors.pin ? (
          <p className="field__error" role="alert">
            {t(errors.pin.message ?? 'form.pin_invalid', {
              defaultValue: t('errors.validation'),
            })}
          </p>
        ) : null}
      </div>

      {/* Custom numeric keypad — 3×4 grid */}
      <div
        role="group"
        aria-label={t('auth.pin')}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}
      >
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button
            key={d}
            type="button"
            disabled={submitting}
            onClick={() => addDigit(d)}
            style={{
              minHeight: 56,
              fontSize: '1.5rem',
              fontFamily: 'var(--font-mono)',
              fontWeight: 600,
              borderRadius: 'var(--radius, 12px)',
              border: '1px solid var(--hairline)',
              background: 'var(--paper)',
              color: 'var(--ink)',
              cursor: 'pointer',
            }}
          >
            {d}
          </button>
        ))}

        {/* Row 4: delete, 0, submit */}
        <button
          type="button"
          aria-label={t('auth.delete_digit')}
          disabled={submitting || digits.length === 0}
          onClick={deleteDigit}
          style={{
            minHeight: 56,
            fontSize: '1.25rem',
            borderRadius: 'var(--radius, 12px)',
            border: '1px solid var(--hairline)',
            background: 'var(--paper)',
            color: 'var(--ink)',
            cursor: 'pointer',
          }}
        >
          ←
        </button>

        <button
          type="button"
          disabled={submitting}
          onClick={() => addDigit('0')}
          style={{
            minHeight: 56,
            fontSize: '1.5rem',
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            borderRadius: 'var(--radius, 12px)',
            border: '1px solid var(--hairline)',
            background: 'var(--paper)',
            color: 'var(--ink)',
            cursor: 'pointer',
          }}
        >
          0
        </button>

        {/* Submit lives inside the keypad so finger travel stays short */}
        <button
          type="submit"
          aria-label={t('auth.submit_pin')}
          disabled={submitting || digits.length < PIN_LENGTH}
          style={{
            minHeight: 56,
            fontSize: '1.25rem',
            fontWeight: 700,
            borderRadius: 'var(--radius, 12px)',
            border: 'none',
            background:
              submitting || digits.length < PIN_LENGTH
                ? 'var(--disabled-bg, #e0e0e0)'
                : 'var(--ink)',
            color:
              submitting || digits.length < PIN_LENGTH
                ? 'var(--disabled-text, #aaa)'
                : 'var(--paper)',
            cursor: submitting || digits.length < PIN_LENGTH ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
          }}
        >
          {submitting ? '…' : '✓'}
        </button>
      </div>

      {serverMessage ? (
        <p className="form__error" role="alert">
          {serverMessage}
        </p>
      ) : null}
    </form>
  );
}
